import { Appearance } from '@channel.io/channel-web-sdk-loader';
import { addQueryParamsUrl } from '@repo/common-utils';
import { useCommonRouter, useWindowOpen } from '@repo/platform';
import { useUserEventLogger } from '@repo/tracking-service';
import { useAuthContext } from '@repo/user-session';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { channelService } from './ChannelService';

export const useInitChannelTalk = (isShowChannelTalkButton: boolean = false, appearance: Appearance = 'light') => {
  const { auth } = useAuthContext();
  const router = useCommonRouter();
  const profile = useMemo(() => {
    if (!auth?.user) return undefined;
    return {
      id: auth.user.id,
      name: auth.user.name,
      email: auth.user.email
    };
  }, [auth?.user]);

  useEffect(() => {
    if (!process.env.NEXT_PUBLIC_CHANNEL_TALK_PLUGIN_KEY) return;

    channelService.boot({
      pluginKey: process.env.NEXT_PUBLIC_CHANNEL_TALK_PLUGIN_KEY,
      hideChannelButtonOnBoot: !isShowChannelTalkButton,
      appearance,
      zIndex: 200,
      profile
    });

    return () => {
      channelService.shutdown();
    };
  }, [appearance, isShowChannelTalkButton, profile]);

  useEffect(() => {
    if (profile) {
      channelService.updateUser({
        profile
      });
    }
  }, [profile]);

  useEffect(() => {
    if (!channelService.isChannelTalkInit) return;
    channelService.setPage(addQueryParamsUrl(router.pathname, router.query as Record<string, string>));
  }, [router.pathname, router.query]);
};

export const useChannelTalk = () => {
  const { openWindow } = useWindowOpen();
  const openChannelTalk = (link: string) => {
    const botId = link.substring(link.lastIndexOf('/') + 1);

    if (channelService.isChannelTalkInit && typeof window.ChannelIO !== 'undefined' && !Number.isNaN(Number(botId))) {
      channelService.openSupportBot(botId);
    } else {
      openWindow(link);
    }
  };

  const showMessenger = () => {
    // channelService가 초기화된 경우에만 메신저를 노출
    if (channelService.isChannelTalkInit && typeof window.ChannelIO !== 'undefined') {
      channelService.showMessenger();
    }
  };

  const hideMessenger = () => {
    if (channelService.isChannelTalkInit && typeof window.ChannelIO !== 'undefined') {
      channelService.hideMessenger();
    }
  };

  return {
    openChannelTalk,
    showMessenger,
    hideMessenger
  };
};

export const useChannelTalkButton = (buttonClickEvent?: string) => {
  const channelButtonRef = useRef<HTMLDivElement>(null);
  const channelMessengerRef = useRef<HTMLDivElement>(null);
  const addChannelButtonClickEventTimer = useRef(0);
  const { sendClickEvent } = useUserEventLogger();

  const channelButtonAction = useCallback(() => {
    if (!channelMessengerRef.current) {
      channelMessengerRef.current = document.querySelector('#ch-plugin-script') as HTMLDivElement;

      if (!channelMessengerRef.current) return;
    }

    const isMessengerOpened = channelMessengerRef.current?.classList.contains('ch-messenger-hidden') ?? false;
    if (!isMessengerOpened) {
      sendClickEvent(buttonClickEvent ?? 'landing_floating_cx');
    }
  }, [buttonClickEvent, sendClickEvent]);

  const addChannelButtonClickEvent = useCallback(() => {
    if (!channelButtonRef.current) {
      channelButtonRef.current = document.querySelector('#ch-plugin-entry') as HTMLDivElement;
    }

    if (channelButtonRef.current) {
      channelButtonRef.current.addEventListener('click', channelButtonAction);
      addChannelButtonClickEventTimer.current = 0;
    } else if (addChannelButtonClickEventTimer.current < 5000) {
      const retryInterval = 100;
      setTimeout(() => {
        addChannelButtonClickEvent();
        addChannelButtonClickEventTimer.current += retryInterval;
      }, retryInterval);
    }
  }, [channelButtonAction]);

  useEffect(() => {
    channelService.showChannelButton();
    addChannelButtonClickEvent();

    return () => {
      channelService.hideChannelButton();
      channelButtonRef.current?.removeEventListener('click', channelButtonAction);
      addChannelButtonClickEventTimer.current = 0;
    };
  }, [addChannelButtonClickEvent, channelButtonAction]);
};
